import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Flame,
  Heart,
  Users,
  Star,
  AlertCircle
} from 'lucide-react';
import { EscortProfile } from '@/types';
import type { LucideIcon } from 'lucide-react';

// Re-using DetailItem component logic (consider moving to a shared utils file if used elsewhere)
const DetailItem: React.FC<{
  Icon: LucideIcon;
  label: string;
  value: React.ReactNode;
  condition?: boolean;
}> = ({ Icon, label, value, condition = true }) => {
  if (!condition || !value) return null;
  return (
    <div className="flex items-center">
      <Icon size={18} className="mr-2 text-pink-500" />
      <span className="text-muted-foreground w-32">{label}:</span>
      <span className="font-medium">{value}</span>
    </div>
  );
};

interface SexualInformationSectionProps {
  profile: EscortProfile;
  canSeeFullProfile: boolean;
  t: (key: string) => string;
}

export const SexualInformationSection: React.FC<SexualInformationSectionProps> = ({ profile, canSeeFullProfile, t }) => {
  const attributes = profile.attributes;

  return (
    <Card className="mb-6 border-pink-100">
      <CardContent className="p-0">
        <div className="bg-pink-50 py-3 px-6 border-b border-pink-100">
          <h3 className="text-lg font-medium text-pink-700">{t('profile.sexual_details')}</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
            <DetailItem Icon={Flame} label={t('orientation')} value={attributes.sexualOrientation} />
            
            {attributes.interestedIn && attributes.interestedIn.length > 0 && (
              <div className="flex items-start">
                <Heart size={18} className="mr-2 text-pink-500 mt-1 flex-shrink-0" />
                <span className="text-muted-foreground w-32 mt-1 flex-shrink-0">{t('interested_in')}:</span>
                <div className="flex flex-wrap gap-1">
                  {attributes.interestedIn.map((interest, idx) => (
                    <Badge key={idx} variant="outline" className="bg-pink-50 text-pink-700 border-pink-200">{interest}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Hardcoded values - Consider making these dynamic if they come from profile data */}
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
            <DetailItem Icon={Users} label={t('preferences')} value={`${t('value_couples')}, ${t('value_singles')}`} />
            <DetailItem Icon={Star} label={t('experience')} value={t('value_intermediate')} />
            <DetailItem Icon={Flame} label={t('kinks')} value={t('value_roleplay_lightbdsm')} />
            <DetailItem Icon={AlertCircle} label={t('limits')} value={t('value_extreme_illegal')} />
          </div>
          
          {!canSeeFullProfile && (
            <div className="mt-4 p-3 bg-pink-50 text-pink-700 rounded-lg">
              <p className="text-center font-medium">
                {t('profile.subscription_required')}
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}; 